import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../components/ui/Toast';
import { useAuth } from '../components/auth/AuthContext';

export default function ExpertMarketplace() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [myRequests, setMyRequests] = useState([]);
  const [showRequests, setShowRequests] = useState(false);

  const CATEGORIES = [
    { key: '', label: '전체' },
    { key: 'learning', label: '📚 학습' },
    { key: 'inspection', label: '🔧 설비점검' },
    { key: 'health', label: '💪 건강/운동' },
  ];

  useEffect(() => {
    loadExperts();
    loadMyRequests();
  }, [selectedCategory]);

  const loadExperts = async () => {
    setLoading(true);
    try {
      let url = '/api/snap/expert/list';
      if (selectedCategory) url += `?category=${selectedCategory}`;
      const response = await fetch(url, { credentials: 'include' });
      const result = await response.json();
      if (result.success) {
        setExperts(result.data || []);
      }
    } catch (error) {
      console.error('Load experts error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMyRequests = async () => {
    try {
      const response = await fetch('/api/snap/expert/request', { credentials: 'include' });
      const result = await response.json();
      if (result.success) {
        setMyRequests(result.data || []);
      }
    } catch (error) {
      console.error('Load requests error:', error);
    }
  };

  const statusLabel = (status) => ({
    pending: '대기중',
    assigned: '배정됨',
    completed: '완료',
    cancelled: '취소',
  }[status] || status);

  const statusColor = (status) => ({
    pending: '#ffc107',
    assigned: '#17a2b8',
    completed: '#28a745',
    cancelled: '#dc3545',
  }[status] || '#666');

  return (
    <div className="page-with-nav" style={{
      minHeight: '100vh',
      background: 'var(--gradient-bg)',
      padding: 'var(--space-lg)',
    }}>
      <div style={{ maxWidth: 'var(--max-width)', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: 'var(--space-lg)',
          animation: 'fadeInDown 0.4s ease forwards',
        }}>
          <button onClick={() => navigate('/')} style={{
            background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: 'white',
          }}>←</button>
          <h1 style={{ fontSize: 'var(--font-size-2xl)', color: 'white', margin: 0 }}>
            👨‍🔬 전문가 마켓플레이스
          </h1>
        </div>

        {/* Toggle: 전문가 목록 / 내 의뢰 */}
        <div style={{
          display: 'flex',
          gap: 'var(--space-sm)',
          marginBottom: 'var(--space-lg)',
        }}>
          <button
            onClick={() => setShowRequests(false)}
            style={{
              flex: 1,
              padding: '0.75rem',
              background: !showRequests ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.08)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              fontWeight: !showRequests ? 700 : 400,
              fontSize: 'var(--font-size-sm)',
            }}
          >
            전문가 목록
          </button>
          <button
            onClick={() => setShowRequests(true)}
            style={{
              flex: 1,
              padding: '0.75rem',
              background: showRequests ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.08)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              fontWeight: showRequests ? 700 : 400,
              fontSize: 'var(--font-size-sm)',
            }}
          >
            내 의뢰 ({myRequests.length})
          </button>
        </div>

        {!showRequests ? (
          <>
            {/* Category filter */}
            <div className="hide-scrollbar" style={{
              display: 'flex',
              gap: 'var(--space-sm)',
              marginBottom: 'var(--space-lg)',
              overflowX: 'auto',
            }}>
              {CATEGORIES.map(cat => (
                <button
                  key={cat.key}
                  onClick={() => setSelectedCategory(cat.key)}
                  style={{
                    padding: '0.5rem 1rem',
                    border: 'none',
                    borderRadius: 'var(--radius-full)',
                    background: selectedCategory === cat.key ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.08)',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: selectedCategory === cat.key ? 700 : 400,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            {/* Expert cards */}
            {loading ? (
              <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.6)', padding: 'var(--space-2xl)' }}>
                Loading...
              </div>
            ) : experts.length === 0 ? (
              <div style={{
                background: 'white',
                borderRadius: 'var(--radius-xl)',
                padding: 'var(--space-2xl)',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '3rem', opacity: 0.4, marginBottom: 'var(--space-md)' }}>👨‍🔬</div>
                <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-lg)' }}>
                  아직 등록된 전문가가 없습니다
                </p>
                <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', marginTop: 'var(--space-sm)' }}>
                  전문가 등록은 곧 오픈됩니다
                </p>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 'var(--space-md)',
              }}>
                {experts.map((expert, i) => (
                  <div key={expert.expert_id} style={{
                    background: 'white',
                    borderRadius: 'var(--radius-xl)',
                    padding: 'var(--space-lg)',
                    boxShadow: 'var(--shadow-md)',
                    opacity: 0,
                    animation: 'fadeInUp 0.4s ease forwards',
                    animationDelay: `${i * 50}ms`,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: 'var(--space-md)' }}>
                      <div style={{
                        width: '48px', height: '48px', borderRadius: '50%',
                        background: 'var(--gradient-accent)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700,
                      }}>
                        {expert.name[0]}
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, color: 'var(--color-text)' }}>{expert.name}</div>
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{expert.specialty}</div>
                      </div>
                      {expert.is_verified ? (
                        <span style={{
                          marginLeft: 'auto',
                          padding: '0.2rem 0.5rem',
                          background: '#28a74518',
                          color: '#28a745',
                          borderRadius: 'var(--radius-full)',
                          fontSize: 'var(--font-size-xs)',
                          fontWeight: 600,
                        }}>인증됨</span>
                      ) : null}
                    </div>
                    {expert.bio && (
                      <p style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-text-secondary)',
                        lineHeight: 1.5,
                        marginBottom: 'var(--space-sm)',
                      }}>{expert.bio}</p>
                    )}
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                      {expert.rating > 0 && <span>⭐ {expert.rating.toFixed(1)} ({expert.review_count})</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          /* My Requests */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
            {myRequests.length === 0 ? (
              <div style={{
                background: 'white',
                borderRadius: 'var(--radius-xl)',
                padding: 'var(--space-2xl)',
                textAlign: 'center',
              }}>
                <p style={{ color: 'var(--color-text-secondary)' }}>의뢰 내역이 없습니다</p>
              </div>
            ) : myRequests.map((req, i) => (
              <div key={req.request_id} style={{
                background: 'white',
                borderRadius: 'var(--radius-xl)',
                padding: 'var(--space-lg)',
                boxShadow: 'var(--shadow-md)',
                opacity: 0,
                animation: 'fadeInUp 0.4s ease forwards',
                animationDelay: `${i * 50}ms`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                    {new Date(req.created_at).toLocaleDateString('ko-KR')}
                  </span>
                  <span style={{
                    padding: '0.2rem 0.6rem',
                    background: statusColor(req.status) + '18',
                    color: statusColor(req.status),
                    borderRadius: 'var(--radius-full)',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 600,
                  }}>
                    {statusLabel(req.status)}
                  </span>
                </div>
                <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text)' }}>
                  시나리오: {req.scenario_type}
                </div>
                {req.message && (
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginTop: '0.25rem' }}>
                    {req.message}
                  </div>
                )}
                {req.expert_response && (
                  <div style={{
                    marginTop: 'var(--space-sm)',
                    padding: 'var(--space-sm)',
                    background: 'var(--color-success-bg)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                  }}>
                    <strong>전문가 답변:</strong> {req.expert_response}
                  </div>
                )}
                <button
                  onClick={() => navigate(`/records/${req.record_id}`)}
                  style={{
                    marginTop: 'var(--space-sm)',
                    padding: '0.4rem 1rem',
                    background: 'var(--color-accent)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 600,
                  }}
                >
                  기록 보기
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
